import classNames from 'classnames'
import React, {
  forwardRef,
  TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import useAutosizeTextArea from '../../hooks/useAutosizeTextArea'
import {checkIsEmptyString} from '../../utils/utils'

export interface TextAreaProps {
  value: string;
  rows?: number;
  placeholder?: string;
  errorMessage?: string;
  fullWidth?: boolean;
  autoResize?: boolean;
  onChange: (value: string) => void;
  props?: TextareaHTMLAttributes<HTMLTextAreaElement>;
}

const TextArea = forwardRef<HTMLTextAreaElement | null, TextAreaProps>(
  (
    {
      value,
      rows = 1,
      placeholder,
      errorMessage = '',
      fullWidth = true,
      autoResize = true,
      onChange,
      props
    },
    ref
  ) => {

    const [showScrollBar, setShowScrollBar] = useState<boolean>(!autoResize)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const textAreaHeight = useRef(0)

    useImperativeHandle(ref, () => textAreaRef.current as HTMLTextAreaElement)
    useAutosizeTextArea(textAreaRef.current, value, autoResize)

    const showError = checkIsEmptyString(value) && !checkIsEmptyString(errorMessage)

    const inputClasses = classNames(
      'bg-white font-normal text-[#2A3344] text-base rounded-xl p-2.5 border focus:outline-none resize-none',
      {
        'border-kwota-alert-300': showError,
        'border-[#E2E8F0] hover:border-[#95B7E4] focus:border-kwota-blue': !showError,
        'w-full': fullWidth
      }
    )

    useEffect(() => {
      if (textAreaRef.current && autoResize) {
        setShowScrollBar(textAreaRef.current.scrollHeight > 116)
      }
    }, [textAreaRef, value, autoResize])

    const handleOnFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      if (textAreaRef.current && autoResize && textAreaHeight.current > 0) {
        event.preventDefault()
        textAreaRef.current.style.height =
          (textAreaHeight.current > 120 ? 120 : textAreaHeight.current) + 'px'
        setShowScrollBar(textAreaRef.current.scrollHeight > 116)
        event.currentTarget.selectionStart = event.currentTarget.value.length
        event.currentTarget.selectionEnd = event.currentTarget.value.length
      }
    }

    const handleOnBlur = (event: React.FocusEvent<HTMLTextAreaElement> ) => {
      props?.onBlur?.(event)
      if (textAreaRef.current && autoResize) {
        textAreaHeight.current = textAreaRef?.current.scrollHeight
        textAreaRef.current.style.height = '44px'
        setShowScrollBar(false)
      }
    }

    return (
      <div>
        <div className="flex items-center w-full">
          <textarea
            {...props}
            ref={textAreaRef}
            rows={autoResize ? 1 : rows}
            value={value}
            onFocus={event => handleOnFocus(event)}
            onBlur={event => handleOnBlur(event)}
            onChange={({target}) => onChange(target.value)}
            className={`${inputClasses} ${
              showScrollBar ? 'overflow-auto' : 'overflow-hidden'
            }`}
            placeholder={placeholder}
          />
        </div>
        {showError &&
          <p className="px-2.5 text-xs font-titillium text-kwota-alert-300">
            {errorMessage}
          </p>
        }
      </div>
    )
  }
)

export default TextArea
